// ─── Tab de permissões granulares com abas verticais e tabela lateral ───
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, HelpCircle, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../api/client';
import { ROUTES } from '../../../api/routes';
import type { Usuario } from './usuarios.types';

import {
  getPermissionsByRole,
  togglePermissionWithCoherence,
  normalizeEscolaCargo,
} from '../../../auth/permissions';

interface Props {
  user: Usuario;
  onUpdated?: () => void;
}

interface RecursoDef {
  label: string;
  desc: string;
  viewKey?: string;
  manageKey?: string;
  viewDesc?: string;
  manageDesc?: string;
  forbiddenCargos?: string[]; // Cargos que nunca visualizam este recurso
  allowedCargos?: string[];   // Cargos exclusivos que podem ver este recurso
  forbiddenManageCargos?: string[]; // Cargos que apenas visualizam e NUNCA gerenciam
}

interface GrupoDef {
  key: string;
  label: string;
  recursos: RecursoDef[];
}

const GRUPOS_PERMISSOES: GrupoDef[] = [
  {
    key: 'equipe_gestao',
    label: 'Equipe e Gestão',
    recursos: [
      {
        label: 'Equipe',
        desc: 'Membros, convites e controle de acesso da equipe escolar/institucional.',
        viewKey: 'equipe.view',
        viewDesc: 'Visualizar a lista de membros e contatos da equipe.',
        manageKey: 'equipe.manage',
        manageDesc: 'Convidar, editar e alterar cargos de membros da equipe.',
        forbiddenCargos: ['COORDENACAO', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Gestão Escolar',
        desc: 'Configurações institucionais, dados cadastrais e parâmetros da escola.',
        manageKey: 'escola.manage',
        manageDesc: 'Alterar dados cadastrais, endereço e logotipo da escola.',
        allowedCargos: ['ADMIN', 'DIRETOR', 'VICE_DIRETOR', 'MASTER', 'MUNICIPAL', 'ESTADUAL', 'REDE'],
      },
      {
        label: 'Painel de Gestão',
        desc: 'Relatórios executivos e indicadores consolidados da gestão.',
        viewKey: 'gestao.view',
        viewDesc: 'Visualizar dashboards executivos da gestão.',
        manageKey: 'gestao.manage',
        manageDesc: 'Ajustar metas e parâmetros do painel executivo.',
        forbiddenCargos: ['PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['SECRETARIA', 'COORDENACAO'],
      },
      {
        label: 'Configurações',
        desc: 'Ajustes avançados, auditoria e preferências do sistema.',
        manageKey: 'configuracoes.manage',
        manageDesc: 'Gerenciar parâmetros avançados do sistema.',
        allowedCargos: ['ADMIN', 'DIRETOR', 'VICE_DIRETOR', 'MASTER', 'MUNICIPAL', 'ESTADUAL', 'REDE'],
      },
    ],
  },
  {
    key: 'academico',
    label: 'Acadêmico',
    recursos: [
      {
        label: 'Turnos',
        desc: 'Horários de entrada, saída, recreios e turnos de atendimento.',
        viewKey: 'turnos.view',
        viewDesc: 'Visualizar turnos cadastrados na escola.',
        manageKey: 'turnos.manage',
        manageDesc: 'Criar, editar e excluir turnos e horários padrão.',
        forbiddenCargos: ['PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['COORDENACAO'],
      },
      {
        label: 'Matérias',
        desc: 'Matriz curricular e disciplinas ofertadas na escola.',
        viewKey: 'materias.view',
        viewDesc: 'Visualizar as matérias da grade curricular.',
        manageKey: 'materias.manage',
        manageDesc: 'Cadastrar, editar e excluir disciplinas escolares.',
        forbiddenCargos: ['RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['PROFESSOR'],
      },
      {
        label: 'Turmas',
        desc: 'Salas de aula, anos/séries e turmas ativas no ano letivo.',
        viewKey: 'turmas.view',
        viewDesc: 'Visualizar turmas e enturmações ativas.',
        manageKey: 'turmas.manage',
        manageDesc: 'Criar turmas, definir capacidade e vincular professores.',
        forbiddenCargos: ['RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['PROFESSOR'],
      },
      {
        label: 'Horários',
        desc: 'Grade semanal de horários e alocação de aulas e professores.',
        viewKey: 'horarios.view',
        viewDesc: 'Consultar a grade horária semanal.',
        manageKey: 'horarios.manage',
        manageDesc: 'Montar, validar e editar a grade de horários da escola.',
        forbiddenCargos: ['RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['PROFESSOR'],
      },
    ],
  },
  {
    key: 'pessoas_contas',
    label: 'Pessoas e Cadastros',
    recursos: [
      {
        label: 'Professores',
        desc: 'Cadastro do corpo docente e dados de contato dos professores.',
        viewKey: 'professores.view',
        viewDesc: 'Visualizar lista e dados dos professores.',
        manageKey: 'professores.manage',
        manageDesc: 'Cadastrar, editar e vincular professores na escola.',
        forbiddenCargos: ['COORDENACAO', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Alunos',
        desc: 'Matrículas, ficha biográfica, fotos e cartões de acesso dos estudantes.',
        viewKey: 'alunos.view',
        viewDesc: 'Visualizar fichas e dados cadastrais dos alunos.',
        manageKey: 'alunos.manage',
        manageDesc: 'Cadastrar novos alunos, transferir e alterar matrículas.',
        forbiddenCargos: ['RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['PROFESSOR'],
      },
      {
        label: 'Responsáveis',
        desc: 'Pais e responsáveis com acesso ao aplicativo da família.',
        viewKey: 'responsaveis.view',
        viewDesc: 'Consultar dados de contato e parentesco com alunos.',
        manageKey: 'responsaveis.manage',
        manageDesc: 'Cadastrar e vincular responsáveis legais a alunos.',
        forbiddenCargos: ['RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['PROFESSOR'],
      },
    ],
  },
  {
    key: 'operacao_portaria',
    label: 'Operação e Portaria',
    recursos: [
      {
        label: 'Chamadas / Frequência',
        desc: 'Presença diária, faltas, atrasos e justificativas de frequência.',
        viewKey: 'chamadas.view',
        viewDesc: 'Visualizar o histórico de presença e faltas.',
        manageKey: 'chamadas.manage',
        manageDesc: 'Realizar chamadas de aula e registrar presença.',
        forbiddenManageCargos: ['RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Dispositivos e RFID',
        desc: 'Leitores de catraca, totens e antenas de controle de acesso.',
        viewKey: 'dispositivos.view',
        viewDesc: 'Monitorar status online dos leitores de presença.',
        manageKey: 'dispositivos.manage',
        manageDesc: 'Parear, configurar e gerenciar dispositivos de portaria.',
        forbiddenCargos: ['COORDENACAO', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Portaria e Acesso',
        desc: 'Monitoramento ao vivo de passagens e catracas nos portões.',
        viewKey: 'acesso.view',
        viewDesc: 'Acompanhar fluxo de entrada/saída em tempo real.',
        manageKey: 'acesso.manage',
        manageDesc: 'Liberar acessos e registrar passagens manuais.',
        forbiddenCargos: ['COORDENACAO', 'PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Incidentes de Segurança',
        desc: 'Ocorrências de portaria, notificações de pânico e alertas operacionais.',
        viewKey: 'security.incidentes-escola.view',
        viewDesc: 'Visualizar alertas e incidentes de segurança escolar.',
        forbiddenCargos: ['PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Dashboard Geral',
        desc: 'Painel inicial com indicadores de presença e resumos do dia.',
        viewKey: 'dashboard.view',
        viewDesc: 'Acessar o painel inicial com estatísticas do dia.',
      },
    ],
  },
  {
    key: 'pedagogico',
    label: 'Pedagógico',
    recursos: [
      {
        label: 'Avaliações e Notas',
        desc: 'Lançamento de notas de provas, trabalhos bimestrais e médias.',
        viewKey: 'avaliacoes.view',
        viewDesc: 'Visualizar boletins e notas lançadas.',
        manageKey: 'avaliacoes.manage',
        manageDesc: 'Lançar, editar e publicar notas de avaliações.',
        forbiddenManageCargos: ['SECRETARIA', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Observações do Aluno',
        desc: 'Registros pedagógicos, histórico disciplinar e elogios ao estudante.',
        viewKey: 'observacoes.view',
        viewDesc: 'Visualizar anotações pedagógicas da turma.',
        manageKey: 'observacoes.manage',
        manageDesc: 'Registrar novas ocorrências pedagógicas na ficha.',
        forbiddenManageCargos: ['SECRETARIA', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Relatórios e Boletins',
        desc: 'Emissão de boletins individuais e relatórios de rendimento escolar.',
        viewKey: 'relatorios.view',
        viewDesc: 'Gerar e imprimir boletins escolares e relatórios analíticos.',
      },
    ],
  },
  {
    key: 'comunicacao_midias',
    label: 'Comunicação e Mídias',
    recursos: [
      {
        label: 'Comunicados e Avisos',
        desc: 'Envio de notificações push e circulares oficiais para as famílias.',
        viewKey: 'comunicados.view',
        viewDesc: 'Visualizar comunicados e circulares emitidos.',
        manageKey: 'comunicados.manage',
        manageDesc: 'Criar e disparar comunicados gerais para a comunidade escolar.',
        forbiddenManageCargos: ['PROFESSOR', 'RESPONSAVEL', 'ALUNO'],
      },
      {
        label: 'Banco de Mídias / TV',
        desc: 'Conteúdos para murais digitais, fotos e TVs dos corredores.',
        viewKey: 'midias.view',
        viewDesc: 'Visualizar galerias e murais digitais da escola.',
        manageKey: 'midias.manage',
        manageDesc: 'Fazer upload de fotos e programar vídeos na TV escolar.',
        forbiddenCargos: ['RESPONSAVEL', 'ALUNO'],
        forbiddenManageCargos: ['PROFESSOR'],
      },
    ],
  },
];

export function UsuariosPermissoesTab({ user, onUpdated }: Props) {
  const [permissoes, setPermissoes] = useState<Record<string, boolean>>({});
  const [preset, setPreset] = useState<string>('PADRAO');
  const [cargo, setCargo] = useState<string>('');
  const [activeGroup, setActiveGroup] = useState<string>('equipe_gestao');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Carrega configurações iniciais garantindo que preferencias estejam presentes
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      let u = user;
      if (!u) return;

      // Se o objeto não possuir preferencias ou se for um objeto superficial, carrega detalhes completos
      if ((u as any).preferencias === undefined && u.id) {
        try {
          const { data: res } = await api.get(`/admin/usuarios/${u.id}`);
          const fullData = res?.data || res;
          if (fullData && isMounted) {
            u = { ...u, ...fullData };
            // Atualiza a referência in-place para que outros hooks vejam
            (user as any).preferencias = fullData.preferencias;
          }
        } catch { /* fallback */ }
      }

      if (isMounted) {
        const prefs = (u as any).preferencias || {};
        const perms = prefs.permissoes || {};
        const presetVal = prefs.permissaoPreset || 'PADRAO';
        const isProf = u.perfil === 'PROFESSOR' || (u as any).cargo === 'PROFESSOR';
        const rawCargo = isProf
          ? 'PROFESSOR'
          : (u.perfil === 'RESPONSAVEL' ? 'RESPONSAVEL' : ((u as any).cargo || (u.perfil === 'ESCOLA' ? 'DIRETOR' : u.perfil || 'DIRETOR')));
        const cargoVal = normalizeEscolaCargo(rawCargo) || rawCargo;

        setCargo(cargoVal);
        setPreset(presetVal);

        if (presetVal === 'PADRAO' || !perms || Object.keys(perms).length === 0) {
          setPermissoes(getPermissionsByRole(isProf ? 'PROFESSOR' : u.perfil, isProf ? 'PROFESSOR' : cargoVal));
        } else {
          setPermissoes(perms);
        }
        setLoaded(true);
      }
    };

    init();
    return () => { isMounted = false; };
  }, [user?.id, (user as any)?.preferencias, (user as any)?.cargo, (user as any)?.perfil]);

  // Normalização consistente do cargo selecionado
  const normalizedCargo = useMemo(() => normalizeEscolaCargo(cargo) || cargo, [cargo]);

  // Checadores de permissão aplicável ao cargo
  const isViewAllowed = useCallback((r: RecursoDef) => {
    return Boolean(r.viewKey);
  }, []);

  const isManageAllowed = useCallback((r: RecursoDef) => {
    if (!r.manageKey) return false;
    if (r.forbiddenManageCargos && r.forbiddenManageCargos.some(fc => normalizeEscolaCargo(fc) === normalizedCargo || fc === normalizedCargo)) {
      return false;
    }
    return true;
  }, [normalizedCargo]);

  // Grupos e recursos visíveis estritamente filtrados pelo cargo do usuário
  const visibleGroups = useMemo(() => {
    return GRUPOS_PERMISSOES.map((g: GrupoDef) => {
      const recursos = g.recursos.filter((r: RecursoDef) => {
        if (
          r.forbiddenCargos &&
          r.forbiddenCargos.some((fc) => normalizeEscolaCargo(fc) === normalizedCargo || fc === normalizedCargo)
        ) {
          return false;
        }
        if (
          r.allowedCargos &&
          !r.allowedCargos.some((ac) => normalizeEscolaCargo(ac) === normalizedCargo || ac === normalizedCargo)
        ) {
          return false;
        }
        return true;
      });
      return { ...g, recursos };
    }).filter((g: GrupoDef) => g.recursos.length > 0);
  }, [normalizedCargo]);

  // Grupo ativo atual garantido dentro dos visíveis
  const currentGroup: GrupoDef = useMemo(() => {
    return visibleGroups.find((g: GrupoDef) => g.key === activeGroup) || visibleGroups[0] || GRUPOS_PERMISSOES[0];
  }, [visibleGroups, activeGroup]);

  // Retorna total de permissões ativas apenas nos recursos visíveis e aplicáveis do grupo
  const getAtivasCount = useCallback(
    (grupoKey: string) => {
      const grp = visibleGroups.find((g) => g.key === grupoKey);
      if (!grp) return 0;
      let count = 0;
      grp.recursos.forEach((r) => {
        if (isViewAllowed(r) && r.viewKey && permissoes[r.viewKey]) count++;
        if (isManageAllowed(r) && r.manageKey && permissoes[r.manageKey]) count++;
      });
      return count;
    },
    [visibleGroups, permissoes, isViewAllowed, isManageAllowed]
  );

  // Alterna uma permissão individual
  const handleTogglePerm = (key: string) => {
    setPermissoes((prev) => togglePermissionWithCoherence(prev, key));
    setPreset('PERSONALIZADO');
  };

  // Altera cargo
  const handleCargoChange = (newCargo: string) => {
    const norm = normalizeEscolaCargo(newCargo) || newCargo;
    setCargo(norm);
    if (preset === 'PADRAO') {
      setPermissoes(getPermissionsByRole(user.perfil, norm));
    }
  };

  // Altera preset
  const handlePresetChange = (newPreset: string) => {
    setPreset(newPreset);
    if (newPreset === 'PADRAO') {
      setPermissoes(getPermissionsByRole(user.perfil, cargo));
    } else if (newPreset === 'COMPLETO') {
      const all: Record<string, boolean> = {};
      visibleGroups.forEach((g) => {
        g.recursos.forEach((r) => {
          if (isViewAllowed(r) && r.viewKey) all[r.viewKey] = true;
          if (isManageAllowed(r) && r.manageKey) all[r.manageKey] = true;
        });
      });
      setPermissoes(all);
    } else if (newPreset === 'SOMENTE_LEITURA') {
      const readonly: Record<string, boolean> = {};
      visibleGroups.forEach((g) => {
        g.recursos.forEach((r) => {
          if (isViewAllowed(r) && r.viewKey) readonly[r.viewKey] = true;
          if (r.manageKey) readonly[r.manageKey] = false;
        });
      });
      setPermissoes(readonly);
    }
  };

  // Salva no backend
  const handleSalvar = async () => {
    setSaving(true);
    try {
      const escolaId = (user as any).escolaId;
      const dataPayload = {
        cargo,
        preferencias: {
          permissaoPreset: preset,
          permissoes: preset === 'PADRAO' ? {} : permissoes,
        },
      };

      if (escolaId) {
        await api.patch(ROUTES.equipe.findOne(escolaId, user.id), dataPayload);
      } else {
        await api.patch(`${ROUTES.admin.usuarios}/${user.id}`, dataPayload);
      }

      // Sincroniza imediatamente o objeto user em memória
      if (user) {
        (user as any).cargo = cargo;
        (user as any).preferencias = {
          ...((user as any).preferencias || {}),
          ...dataPayload.preferencias,
        };
      }

      toast.success('Configurações de permissões salvas com sucesso!');
      onUpdated?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const renderCustomCheckbox = (checked: boolean, onChange: () => void, tooltip?: string) => (
    <button
      type="button"
      onClick={onChange}
      title={tooltip}
      style={{
        width: 18,
        height: 18,
        borderRadius: 4,
        border: checked ? '2px solid #6366f1' : '1.5px solid #94a3b8',
        background: checked ? '#6366f1' : '#ffffff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        transition: 'all .15s ease',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );

  // Render loading state após TODOS os hooks para cumprir estritamente Rules of Hooks
  if (!loaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40, color: '#9ca3af', gap: 8 }}>
        <Loader2 className="animate-spin" size={24} />
        <span style={{ fontSize: '.85rem' }}>Carregando permissões...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Topo: Seletores de Perfil e Preset */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Perfil de Acesso */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Perfil / Cargo *
            <span
              title="Define o papel operacional e os recursos liberados neste perfil"
              style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
            >
              <HelpCircle size={14} style={{ color: '#9ca3af' }} />
            </span>
          </label>
          <select
            className="form-select"
            value={cargo}
            onChange={(e) => handleCargoChange(e.target.value)}
          >
            <optgroup label="Escola / Equipe Escolar">
              <option value="DIRETOR">DIRETOR (Acesso Total da Escola)</option>
              <option value="VICE_DIRETOR">VICE DIRETOR (Acesso Total da Escola)</option>
              <option value="COORDENACAO">COORDENAÇÃO (Pedagógico e Acadêmico)</option>
              <option value="SECRETARIA">SECRETARIA (Administrativo e Cadastros)</option>
            </optgroup>
            <optgroup label="Corpo Docente">
              <option value="PROFESSOR">PROFESSOR (Diário, Chamadas e Notas)</option>
            </optgroup>
            <optgroup label="Gestão Municipal">
              <option value="MUNICIPAL">GESTOR MUNICIPAL (SME)</option>
              <option value="COMUNICACAO">COMUNICAÇÃO MUNICIPAL</option>
              <option value="CONSULTA">CONSULTA MUNICIPAL</option>
            </optgroup>
            <optgroup label="Gestão Estadual">
              <option value="ESTADUAL">GESTOR ESTADUAL (SEE)</option>
              <option value="ANALISTA">ANALISTA ESTADUAL</option>
            </optgroup>
            <optgroup label="Gestão de Rede">
              <option value="REDE">GESTOR DE REDE</option>
              <option value="SUPERVISOR">SUPERVISOR DE REDE</option>
            </optgroup>
            <optgroup label="Plataforma e Comunidade">
              <option value="ADMIN">ADMINISTRADOR MASTER</option>
              <option value="RESPONSAVEL">RESPONSÁVEL (Acesso Pais)</option>
              <option value="ALUNO">ALUNO (Acesso Estudante)</option>
            </optgroup>
          </select>
        </div>

        {/* Configuração de Permissões */}
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            Configuração de Permissões
            <span
              title="Escolha um modelo padrão de permissões ou personalize individualmente"
              style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
            >
              <HelpCircle size={14} style={{ color: '#9ca3af' }} />
            </span>
          </label>
          <select
            className="form-select"
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value)}
          >
            <option value="PADRAO">Seguir permissões padrão do perfil</option>
            <option value="COMPLETO">Completo (Acesso total permitido ao cargo)</option>
            <option value="SOMENTE_LEITURA">Somente Leitura</option>
            <option value="PERSONALIZADO">Personalizar permissões</option>
          </select>
        </div>
      </div>

      {/* Meio: Abas Verticais (Esquerda) + Tabela de Recursos (Direita) */}
      <div
        style={{
          display: 'flex',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
          overflow: 'hidden',
          background: '#fff',
          minHeight: 300,
        }}
      >
        {/* Abas Verticais (Esquerda) */}
        <div
          style={{
            width: 200,
            background: '#f8fafc',
            borderRight: '1px solid #e2e8f0',
            padding: '12px 8px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {visibleGroups.map((g) => {
            const isSelected = currentGroup.key === g.key;
            const count = getAtivasCount(g.key);

            return (
              <button
                key={g.key}
                type="button"
                onClick={() => setActiveGroup(g.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 12px',
                  borderRadius: 6,
                  border: 'none',
                  textAlign: 'left',
                  background: isSelected ? '#fff' : 'transparent',
                  color: isSelected ? '#6366f1' : '#4b5563',
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: '.82rem',
                  cursor: 'pointer',
                }}
              >
                <span>{g.label}</span>
                {count > 0 && (
                  <span
                    style={{
                      minWidth: 18,
                      height: 18,
                      borderRadius: 9,
                      background: isSelected ? '#e0e7ff' : '#f3f4f6',
                      color: isSelected ? '#4f46e5' : '#6b7280',
                      fontSize: '.68rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tabela de Recursos (Direita) */}
        <div style={{ flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 14px' }}>
            <h3
              style={{
                fontSize: '.9rem',
                fontWeight: 800,
                color: '#1e293b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                margin: 0,
              }}
            >
              {currentGroup.label}
            </h3>
            <span style={{ fontSize: '.74rem', color: '#64748b' }}>
              Passe o cursor sobre os ícones <HelpCircle size={12} style={{ display: 'inline', verticalAlign: '-1px' }} /> para ver detalhes
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.83rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                <th style={{ textAlign: 'left', paddingBottom: 10, color: '#64748b', fontWeight: 600 }}>RECURSO</th>
                <th style={{ textAlign: 'center', paddingBottom: 10, color: '#64748b', fontWeight: 600, width: 100 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    VISUALIZAR
                    <span title="Permissão para consultar/ler dados deste recurso" style={{ cursor: 'help' }}>
                      <HelpCircle size={12} style={{ color: '#9ca3af' }} />
                    </span>
                  </span>
                </th>
                <th style={{ textAlign: 'center', paddingBottom: 10, color: '#64748b', fontWeight: 600, width: 100 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    GERENCIAR
                    <span title="Permissão para criar, editar, alterar ou excluir dados deste recurso" style={{ cursor: 'help' }}>
                      <HelpCircle size={12} style={{ color: '#9ca3af' }} />
                    </span>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {currentGroup.recursos.map((r: RecursoDef, index: number) => {
                const canView = isViewAllowed(r);
                const canManage = isManageAllowed(r);

                return (
                  <tr key={index} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '10px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#1e293b', fontWeight: 600 }}>{r.label}</span>
                        <span title={r.desc} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}>
                          <HelpCircle size={13} style={{ color: '#9ca3af' }} />
                        </span>
                      </div>
                      {r.desc && (
                        <div style={{ fontSize: '.72rem', color: '#64748b', marginTop: 1, lineHeight: 1.3 }}>
                          {r.desc}
                        </div>
                      )}
                    </td>

                    {/* Visualizar Checkbox */}
                    <td style={{ textAlign: 'center', padding: '10px 0' }}>
                      {canView && r.viewKey ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {renderCustomCheckbox(
                            !!permissoes[r.viewKey],
                            () => handleTogglePerm(r.viewKey!),
                            r.viewDesc || 'Permitir visualização'
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1', fontSize: '.75rem' }} title="Não aplicável para este recurso">-</span>
                      )}
                    </td>

                    {/* Gerenciar Checkbox */}
                    <td style={{ textAlign: 'center', padding: '10px 0' }}>
                      {canManage && r.manageKey ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {renderCustomCheckbox(
                            !!permissoes[r.manageKey],
                            () => handleTogglePerm(r.manageKey!),
                            r.manageDesc || 'Permitir criação e edição'
                          )}
                        </div>
                      ) : (
                        <span
                          style={{
                            color: '#cbd5e1',
                            fontSize: '.75rem',
                            cursor: 'help',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                          title="Ação de gerenciamento/cadastro não disponível para este perfil"
                        >
                          -
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Botão Salvar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <button
          className="btn-save"
          onClick={handleSalvar}
          disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Save size={14} />
          {saving ? 'Salvando...' : 'Salvar Permissões'}
        </button>
      </div>
    </div>
  );
}
